import {
  DialogsStack,
  NotificationsTicker,
  Revision,
} from "@neru/base/components";
import { mainNavigationConfig } from "@neru/base/config";
import {
  getActiveMenuItem,
  MAIN_NAVIGATION_HEIGHT,
  MainNavigation,
  MENU_KEYS,
} from "@neru/base/features/MainNavigation";
import {
  SecureRouteWithCookie,
  SecureRouteWithToken,
} from "@neru/base/features/Security";
import { useAuth, useCurrentAccount } from "@neru/base/hooks";
import { componentLoader, getIsProxy, MFA } from "@neru/base/utils";
import { Banner, Layout, Spinner } from "@neru/ui";
import { SplitClient, SplitFactory } from "@splitsoftware/splitio-react";
import { lazy, Suspense, useEffect } from "react";
import { Outlet, Route, Routes, useLocation } from "react-router-dom";

import * as S from "./styles";
import { shouldTrack, trackPageView } from "./utils/AdobeAnalytics";
import { config } from "./utils/config";

const Home = lazy(() => componentLoader(() => import("./features/Home/Home")));
const Product = lazy(() =>
  componentLoader(() => import("./features/Product/Product")),
);
const ProductVersions = lazy(() =>
  componentLoader(() => import("./features/Product/ProductVersions")),
);
const ProductClients = lazy(() =>
  componentLoader(() => import("./features/Product/ProductClients")),
);
const PublishPage = lazy(() =>
  componentLoader(() => import("./features/Product/PublishPage")),
);
const AccountOverview = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/AccountOverview/AccountOverview"),
  ),
);
const PlansAndPricing = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/PlansAndPricing/PlansAndPricing"),
  ),
);

const DeployedProducts = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/DeployedProducts/DeployedProducts"),
  ),
);
const DeployedProduct = lazy(() =>
  componentLoader(
    () =>
      import(
        "@neru/base/features/DeployedProducts/DeployedProduct/DeployedProduct"
      ),
  ),
);

const Instances = lazy(() =>
  componentLoader(() => import("@neru/base/features/Instances/Instances")),
);
const Instance = lazy(() =>
  componentLoader(() => import("@neru/base/features/Instances/Instance")),
);

const Workspaces = lazy(() =>
  componentLoader(() => import("@neru/base/features/Workspaces/Workspaces")),
);
const CreateWorkspace = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/CreateWorkspace/CreateWorkspace"),
  ),
);
const Workspace = lazy(() =>
  componentLoader(() => import("@neru/base/features/Workspaces/Workspace")),
);

const App = ({ hasPortalSession }: { hasPortalSession: boolean }) => {
  const SecureRoute =
    config.isMFA && getIsProxy() ? SecureRouteWithCookie : SecureRouteWithToken;

  const { accountId } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const { isBanned } = useCurrentAccount();

  useEffect(() => {
    if (shouldTrack(pathname)) {
      trackPageView();
      MFA.notifyPageReady();
    }
  }, [pathname]);

  return (
    <Layout className="p-0">
      <S.Container id="container">
        <SplitFactory config={config.splitConfig}>
          <SplitClient
            splitKey={
              accountId || localStorage.getItem("vch.account_id") || "key"
            }
          >
            <>
              <MainNavigation
                active={getActiveMenuItem(pathname) ?? MENU_KEYS.market}
                logo={
                  <a href={mainNavigationConfig.urlMarket}>
                    <S.Logo>
                      <div />
                      <div />
                      <div />
                      <div />
                    </S.Logo>
                  </a>
                }
                theme={config.isMFA ? "light" : "dark"}
                withAccountSelector
                withRegionSelector
                withSignIn={!config.isMFA && !config.skipAuth}
              />

              <S.Column
                flexGrow={1}
                id="main-content"
                justifyContent="flex-start"
                style={{ marginTop: MAIN_NAVIGATION_HEIGHT, zIndex: 0 }}
              >
                {isBanned ? (
                  <Banner
                    connotation="alert"
                    data-testid="alert-blocked-banner"
                    message="This account has been blocked. Please contact support."
                    open
                  />
                ) : (
                  <Suspense fallback={<Spinner />}>
                    <Routes>
                      <Route
                        element={
                          config.skipAuth ? (
                            <Outlet />
                          ) : (
                            <SecureRoute
                              hasPortalSession={
                                config.isMFA ? hasPortalSession : undefined
                              }
                            />
                          )
                        }
                      >
                        <Route element={<AccountOverview />} path="/overview" />
                        <Route
                          element={<PlansAndPricing />}
                          path="/plans-and-pricing"
                        />
                        <Route element={<Instances />} path="/instances" />
                        <Route element={<Workspaces />} path="/workspaces" />
                        <Route
                          element={<CreateWorkspace />}
                          path="/workspaces/create"
                        />
                        <Route
                          element={<Workspace />}
                          path="/workspaces/:workspaceId"
                        />
                        <Route
                          element={<Instance />}
                          path="/instances/:instanceServiceName"
                        />
                        <Route
                          element={<DeployedProducts />}
                          path="/deployed-products"
                        />
                        <Route
                          element={<DeployedProduct />}
                          path="/deployed-products/:productId"
                        />
                        <Route element={<PublishPage />} path="/publish" />
                        <Route
                          element={<PublishPage />}
                          path="/publish/:productId"
                        />
                        <Route
                          element={<PublishPage />}
                          path="/:productId/versions/:versionId"
                        />
                        <Route
                          element={<ProductVersions />}
                          path="/:productId/versions"
                        />
                        <Route
                          element={<ProductClients />}
                          path="/:productId/clients"
                        />
                      </Route>

                      <Route
                        element={
                          hasPortalSession ? <SecureRoute /> : <Outlet />
                        }
                      >
                        <Route element={<Home />} path="/" />
                        <Route element={<Product />} path="/:productId" />
                        <Route
                          element={<Product documentation />}
                          path="/:productId/documentation"
                        />
                        <Route
                          element={<Product releases />}
                          path="/:productId/releases"
                        />
                      </Route>
                    </Routes>
                  </Suspense>
                )}
              </S.Column>
            </>
          </SplitClient>
        </SplitFactory>
      </S.Container>
      <div id="documentation" style={{ display: "none" }}></div>

      <DialogsStack />
      <NotificationsTicker />
      <Revision />
    </Layout>
  );
};

export default App;
